import { useEffect, useState } from 'react';
import { DEVISE, fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { tauxTvaProposes } from '../../lib/tva';
import { C } from '../../theme/palette';

/**
 * Lit un pourcentage saisi au clavier (« 12,5 », « 30 », « » …).
 *
 * <p>La virgule décimale suisse est admise ; une saisie vide ou illisible vaut
 * zéro, et la valeur reste entre 0 et 100 : une remise de 120 % rendrait le
 * total négatif.
 *
 * @param {string|number} valeur la saisie
 * @returns {number} le pourcentage, entre 0 et 100
 */
export function lisPourcent(valeur) {
  const n = +String(valeur ?? '').replace(',', '.');
  if (!Number.isFinite(n) || n <= 0) {
    return 0;
  }
  return Math.min(100, n);
}

const etiquette = {
  fontSize: 9,
  fontWeight: 700,
  color: C.muted,
  marginBottom: 5,
  textTransform: 'uppercase',
};

/**
 * Champ numérique « [30] % » de la maquette : acompte à la commande, remise
 * globale.
 *
 * @param {object} props propriétés du composant
 * @param {string} props.label libellé au-dessus du champ
 * @param {number} props.value pourcentage
 * @param {(n: number) => void} props.onChange reçoit le pourcentage lu
 * @param {string} [props.placeholder] texte du champ vide
 * @param {boolean} [props.videSiZero] affiche un champ vide plutôt que 0
 * @returns {import('react').ReactElement} le champ
 */
export function ChampPourcent({ label, value, onChange, placeholder, videSiZero }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={etiquette}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          aria-label={label}
          value={videSiZero && !value ? '' : (value ?? 0)}
          inputMode="decimal"
          placeholder={placeholder}
          onChange={(e) => onChange(lisPourcent(e.target.value))}
          style={{
            width: 64,
            textAlign: 'center',
            fontWeight: 800,
            fontSize: 16,
            border: '1px solid ' + C.border,
            borderRadius: 9,
            padding: '9px',
            color: C.ink,
          }}
        />
        <span style={{ fontSize: 14, fontWeight: 800, color: C.grey }}>%</span>
      </div>
    </div>
  );
}

/**
 * Lit un montant saisi au clavier (« 5'000 », « 5000.50 », « 5 000,50 »).
 *
 * @param {string|number} valeur la saisie
 * @returns {number} le montant, zéro si illisible ou négatif
 */
export function lisMontant(valeur) {
  const brut = String(valeur ?? '')
    .replace(/[\s'’]/g, '')
    .replace(',', '.');
  const n = +brut;
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
}

/**
 * Pourcentage du total qu'un acompte en francs représente, au centième, comme
 * le serveur : 5'000.00 sur 16'371.51 → 30.54.
 *
 * @param {number} montant acompte TTC
 * @param {number} ttc total TTC de l'offre
 * @returns {number} le pourcentage, entre 0 et 100
 */
export function pourcentDuMontant(montant, ttc) {
  if (!(+montant > 0) || !(+ttc > 0)) return 0;
  return Math.min(100, Math.round(((+montant * 100) / +ttc) * 100) / 100);
}

const segment = (actif) => ({
  padding: '9px 11px',
  fontSize: 13,
  fontWeight: 800,
  border: 0,
  cursor: 'pointer',
  background: actif ? C.navy : C.white,
  color: actif ? C.white : C.muted,
});

/**
 * « Acompte à la commande » : en pour-cent ou en francs, au choix (maquette du
 * 29.10). En francs, le montant écrit TVA comprise fait foi — 5'000.00 donne
 * une facture d'acompte de 5'000.00 — et le pourcentage se lit en dessous.
 *
 * @param {object} props propriétés du composant
 * @param {string} props.label libellé au-dessus du champ
 * @param {number} props.pourcent acompte en pour-cent
 * @param {number|null} props.montant acompte en francs, ou vide
 * @param {boolean} props.enFrancs le champ est en francs
 * @param {number} props.ttc total TTC de l'offre
 * @param {number} props.calcule montant de l'acompte tel qu'il sera facturé
 * @param {boolean} [props.surFacture] le champ est sur une facture, pas sur une offre
 * @param {(v: {acompte: number, acompteMontant: number|null, acompteEnFrancs: boolean}) => void} props.onChange
 *   reçoit l'acompte lu
 * @returns {import('react').ReactElement} le champ
 */
export function ChampAcompte({
  label,
  pourcent,
  montant,
  enFrancs,
  ttc,
  calcule,
  onChange,
  surFacture = false,
}) {
  const [texte, setTexte] = useState(montant > 0 ? String(montant) : '');
  // L'offre ouverte ou le mode changé de l'extérieur : le champ reprend le montant.
  useEffect(() => {
    setTexte((t) => (lisMontant(t) === (+montant || 0) ? t : montant > 0 ? String(montant) : ''));
  }, [montant]);
  const choisit = (francs) => {
    if (francs === enFrancs) return;
    if (francs) {
      // Le montant d'aujourd'hui pour départ : rien ne change sans qu'on l'écrive.
      const m = calcule > 0 ? Math.round(calcule * 100) / 100 : null;
      setTexte(m ? String(m) : '');
      onChange({ acompte: pourcent || 0, acompteMontant: m, acompteEnFrancs: true });
    } else {
      onChange({ acompte: pourcent || 0, acompteMontant: null, acompteEnFrancs: false });
    }
  };
  return (
    <div style={{ flex: 1.4 }}>
      <div style={etiquette}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <div
          role="group"
          aria-label={tr('Acompte en % ou en francs')}
          style={{
            display: 'inline-flex',
            border: '1px solid ' + C.border,
            borderRadius: 9,
            overflow: 'hidden',
          }}
        >
          <button
            type="button"
            aria-pressed={!enFrancs}
            onClick={() => choisit(false)}
            style={segment(!enFrancs)}
          >
            %
          </button>
          <button
            type="button"
            aria-pressed={enFrancs}
            onClick={() => choisit(true)}
            style={segment(enFrancs)}
          >
            {DEVISE}
          </button>
        </div>
        <input
          aria-label={label}
          inputMode="decimal"
          value={enFrancs ? texte : pourcent ? pourcent : ''}
          onChange={(e) => {
            if (enFrancs) {
              setTexte(e.target.value);
              const m = lisMontant(e.target.value);
              onChange({
                acompte: pourcentDuMontant(m, ttc),
                acompteMontant: m > 0 ? m : null,
                acompteEnFrancs: true,
              });
            } else {
              onChange({
                acompte: lisPourcent(e.target.value),
                acompteMontant: null,
                acompteEnFrancs: false,
              });
            }
          }}
          style={{
            width: enFrancs ? 104 : 64,
            textAlign: 'center',
            fontWeight: 800,
            fontSize: 16,
            border: '1px solid ' + C.border,
            borderRadius: 9,
            padding: '9px',
            color: C.ink,
          }}
        />
      </div>
      {calcule > 0 && (
        <div style={{ fontSize: 10.5, color: C.teal, marginTop: 5 }}>
          {enFrancs
            ? trf(
                surFacture
                  ? '= {0} % de la facture ({1} {2} TVA comprise)'
                  : '= {0} % de l’offre ({1} {2} TVA comprise)',
                pourcentDuMontant(calcule, ttc),
                DEVISE,
                fmt(ttc),
              )
            : '= ' + DEVISE + ' ' + fmt(calcule)}
        </div>
      )}
    </div>
  );
}

/**
 * Le taux de TVA du document, choisi dans le récapitulatif : la ligne
 * « TVA 8.1% » du total devient une liste déroulante discrète. La maquette n'a
 * pas de sélecteur de TVA dans le formulaire ; le taux par défaut vient des
 * réglages et chaque ligne peut porter le sien.
 *
 * @param {object} props propriétés du composant
 * @param {object} [props.soc] société (taux proposés)
 * @param {string} [props.date] date du document : un devis de 2023 propose les taux de 2023
 * @param {number} props.value taux du document
 * @param {(t: number) => void} props.onChange reçoit le taux choisi
 * @param {object} [props.style] style du texte
 * @returns {import('react').ReactElement} la liste
 */
export function ChoixTauxTva({ soc, date, value, onChange, style }) {
  return (
    <select
      aria-label="Taux de TVA du document"
      value={String(value)}
      onChange={(e) => onChange(+e.target.value)}
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        ...style,
      }}
    >
      {tauxTvaProposes(soc, date).map((t) => (
        <option key={t} value={String(t)} style={{ color: C.ink }}>
          TVA {t}% ▾
        </option>
      ))}
    </select>
  );
}
